/**
 * Vault invitations API functions
 */

import { authenticatedRequest } from "./client";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

export interface VaultInvitation {
  id: number;
  vault_id: number;
  invited_email: string;
  invited_role: "VAULT_CO_ADMIN" | "VAULT_MEMBER";
  token: string;
  status: "pending" | "accepted" | "expired" | "revoked";
  created_by_user_id: string;
  accepted_by_user_id: string | null;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  invitation_link: string;
}

export interface CreateInvitationRequest {
  email: string;
  role: "VAULT_CO_ADMIN" | "VAULT_MEMBER";
  expires_in_days?: number;
}

export interface InvitationValidation {
  valid: boolean;
  error?: string;
  vault_name?: string;
  vault_id?: number;
  inviter_name?: string;
  role?: string;
  expires_at?: string;
}

export interface AcceptInvitationResponse {
  vault_id: number;
  vault_name: string;
  role: string;
}

/**
 * Create an invitation to join a vault
 */
export async function createInvitation(
  vaultId: number,
  data: CreateInvitationRequest,
  token: string
): Promise<VaultInvitation> {
  return authenticatedRequest<VaultInvitation>(
    {
      method: "POST",
      url: `/vaults/${vaultId}/invitations`,
      data,
    },
    token
  );
}

/**
 * List invitations for a vault
 */
export async function listInvitations(
  vaultId: number,
  token: string,
  statusFilter?: "pending" | "accepted" | "expired" | "revoked"
): Promise<VaultInvitation[]> {
  const params = new URLSearchParams();
  if (statusFilter) {
    params.set("status_filter", statusFilter);
  }

  return authenticatedRequest<VaultInvitation[]>(
    {
      method: "GET",
      url: `/vaults/${vaultId}/invitations${
        params.toString() ? `?${params.toString()}` : ""
      }`,
    },
    token
  );
}

/**
 * Revoke an invitation
 */
export async function revokeInvitation(
  vaultId: number,
  invitationId: number,
  token: string
): Promise<void> {
  return authenticatedRequest<void>(
    {
      method: "DELETE",
      url: `/vaults/${vaultId}/invitations/${invitationId}`,
    },
    token
  );
}

/**
 * Resend an invitation (generates new token)
 */
export async function resendInvitation(
  vaultId: number,
  invitationId: number,
  token: string
): Promise<VaultInvitation> {
  return authenticatedRequest<VaultInvitation>(
    {
      method: "POST",
      url: `/vaults/${vaultId}/invitations/${invitationId}/resend`,
    },
    token
  );
}

/**
 * Validate an invitation token (public, no auth required)
 */
export async function validateInvitation(
  token: string
): Promise<InvitationValidation> {
  const response = await fetch(`${API_URL}/invitations/${token}/validate`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to validate invitation");
  }

  return response.json();
}

/**
 * Accept an invitation and join the vault
 */
export async function acceptInvitation(
  invitationToken: string,
  authToken: string
): Promise<AcceptInvitationResponse> {
  return authenticatedRequest<AcceptInvitationResponse>(
    {
      method: "POST",
      url: `/invitations/${invitationToken}/accept`,
    },
    authToken
  );
}
